import { useState } from "react";

interface NoteCategory {
  id: number;
  title: string;
  notes: string[];
}

export function TeamNotes() {
  const [categories, setCategories] = useState<NoteCategory[]>([
    {
      id: 1,
      title: "General Strategy",
      notes: ["Focus on mid-round calling", "Improve utility usage"],
    },
    {
      id: 2,
      title: "Player Availability",
      notes: ["John unavailable on Fridays", "Scrim schedule updated"],
    },
  ]);

  const [newCategory, setNewCategory] = useState("");
  const [newNote, setNewNote] = useState<Record<number, string>>({});

  function addCategory() {
    if (!newCategory.trim()) return;

    setCategories((prev) => [
      ...prev,
      { id: Date.now(), title: newCategory, notes: [] },
    ]);

    setNewCategory("");
  }

  function addNote(categoryId: number) {
    const text = newNote[categoryId];
    if (!text || !text.trim()) return;

    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, notes: [...cat.notes, text] }
          : cat
      )
    );

    setNewNote((prev) => ({ ...prev, [categoryId]: "" }));
  }

  function deleteNote(categoryId: number, noteIndex: number) {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              notes: cat.notes.filter((_, i) => i !== noteIndex),
            }
          : cat
      )
    );
  }

  return (
    <div className="space-y-10">

      {/* PAGE HEADER */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Team Notes</h1>
        <p className="text-muted">
          Internal intelligence for shared team communication, planning, and reminders.
        </p>
      </div>

      {/* ADD CATEGORY */}
      <section className="bg-surface border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Add New Category</h2>

        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="e.g., Scrim Notes, Strategy Ideas, Player Feedback"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="
              flex-grow bg-bg border border-border rounded-md p-3
              focus:outline-none focus:border-[#4FC3F7]
            "
          />

          <button
            onClick={addCategory}
            className="
              px-6 py-3 rounded-md font-semibold
              bg-[#4FC3F7] text-[#0C0F14]
              hover:bg-[#81D4FA] transition
            "
          >
            Add
          </button>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">

        {categories.map((cat) => (
          <section
            key={cat.id}
            className="bg-surface border border-border rounded-lg p-6 flex flex-col"
          >
            {/* CATEGORY TITLE */}
            <h2 className="text-lg font-semibold mb-4">{cat.title}</h2>

            {/* NOTES LIST */}
            <div className="space-y-3 mb-4">
              {cat.notes.length === 0 && (
                <p className="text-muted text-sm">No notes yet.</p>
              )}

              {cat.notes.map((note, index) => (
                <div
                  key={index}
                  className="bg-bg border border-border rounded-md p-3 flex justify-between items-start"
                >
                  <p className="text-sm">{note}</p>

                  <button
                    onClick={() => deleteNote(cat.id, index)}
                    className="text-xs text-muted hover:text-red-400 transition ml-3"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>

            {/* ADD NOTE */}
            <textarea
              placeholder="Add a note..."
              value={newNote[cat.id] || ""}
              onChange={(e) =>
                setNewNote((prev) => ({
                  ...prev,
                  [cat.id]: e.target.value,
                }))
              }
              className="
                w-full min-h-24 bg-bg border border-border rounded-md p-3
                focus:outline-none focus:border-[#4FC3F7]
                resize-y
              "
            />

            <button
              onClick={() => addNote(cat.id)}
              className="
                mt-4 px-4 py-2 rounded-md font-medium
                bg-[#4FC3F7] text-[#0C0F14]
                hover:bg-[#81D4FA] transition
              "
            >
              Add Note
            </button>
          </section>
        ))}

      </div>
    </div>
  );
}